/**
 * The secured-floor interlock as a channel-modifier program (schema v8).
 *
 * A site keeps every floor secured by a relay. When the controller is told to
 * energize a car call to a secured floor, the modifier holds the call,
 * unsecures the floor, waits for the read-back input to confirm it, releases
 * the call, follows the car to the floor and through its door cycle, and
 * secures the floor again — always, even when an earlier step failed. A
 * precondition makes the whole thing apply only while the floor is secured
 * now, and a gate that cannot confirm the unsecure drops the call and
 * re-secures.
 *
 * Pure TypeScript with no React in its import graph: the setup wizard's
 * template dialog loads the result into the Channel Modifiers form, and the
 * secured-floor demo's seeder (run with ts-node) packs it straight to
 * LiftCloud. Both get the same generated Object API value.
 */
import { ActiveState } from "../generated/archive/active-state";
import { ChannelModifierT } from "../generated/archive/channel-modifier";
import { ConditionOp } from "../generated/archive/condition-op";
import { ConflictPolicy } from "../generated/archive/conflict-policy";
import { ModifierConditionT } from "../generated/archive/modifier-condition";
import { ModifierDirection } from "../generated/archive/modifier-direction";
import { ModifierDropAction } from "../generated/archive/modifier-drop-action";
import { ModifierSequenceStepT } from "../generated/archive/modifier-sequence-step";
import { ModifierStateConditionT } from "../generated/archive/modifier-state-condition";
import { ModifierTimeoutPolicy } from "../generated/archive/modifier-timeout-policy";
import { ModifierType } from "../generated/archive/modifier-type";
import { PreconditionUnknown } from "../generated/archive/precondition-unknown";
import { SequenceStepKind } from "../generated/archive/sequence-step-kind";
import { SequenceStepScope } from "../generated/archive/sequence-step-scope";
import { StepPositionSource } from "../generated/archive/step-position-source";
import { ValueTransformMode } from "../generated/archive/value-transform-mode";
import { Control } from "../generated/control/control";
import { MessageContent } from "../generated/ln2/message-content";
import { Traffic } from "../generated/traffic/traffic";
import { Usage } from "../generated/usage/usage";
import { stringToUuidT, ZERO_UUID_STRING } from "../../utils/uuid";
import { buildMessageT, payloadDraftFromLeaf } from "./channelModifierPayloadBuilder";

/** Which car call the interlock serves: the front one or the rear one. */
export type SecuredFloorVariant = "front" | "rear";

/** The schema version every secured-floor modifier carries: it is a v8 program. */
export const SECURED_FLOOR_SCHEMA_VERSION = 8;

/** What a site supplies; everything else is the program itself. */
export interface SecuredFloorParams {
  variant: SecuredFloorVariant;
  /** The modifier's name, as the step's table and exports show it. */
  name: string;
  /** The elevator controller's channel (LN1): the source of the car calls. */
  elevatorChannelId: string;
  /** The relay channel that secures and unsecures each floor: the modifier's target. */
  relayChannelId: string;
  /** The channel whose input reads each floor's security back; it may be the relay channel. */
  discreteChannelId: string;
  /**
   * The Control leaf that secures a floor: SecureCarCall, one lock per floor
   * for front and rear calls alike, unless the site secures rear calls
   * separately (SecureRearCarCall).
   */
  securityLeaf?: Control;
  /** The modifier's id, for an update; omitted, the zero UUID asks LiftCloud for a new one. */
  id?: string;
}

/** The leaves, and their step labels, that differ between a front call and a rear one. */
interface CallLeaves {
  energize: Control;
  carCall: Traffic;
  doorOpened: Usage;
  doorClosed: Usage;
  carCallLabel: string;
  doorOpenedLabel: string;
  doorClosedLabel: string;
}

const CALL_LEAVES: Record<SecuredFloorVariant, CallLeaves> = {
  front: {
    energize: Control.EnergizeCarCall,
    carCall: Traffic.CarCall,
    doorOpened: Usage.FrontDoorOpened,
    doorClosed: Usage.FrontDoorClosed,
    carCallLabel: "Car call registered",
    doorOpenedLabel: "Doors opened",
    doorClosedLabel: "Doors closed",
  },
  rear: {
    energize: Control.EnergizeRearCarCall,
    carCall: Traffic.RearCarCall,
    doorOpened: Usage.RearDoorOpened,
    doorClosed: Usage.RearDoorClosed,
    carCallLabel: "Rear car call registered",
    doorOpenedLabel: "Rear doors opened",
    doorClosedLabel: "Rear doors closed",
  },
};

/** A step on the generated Object API; every field not named keeps its schema default. */
function programStep(fields: Partial<ModifierSequenceStepT>): ModifierSequenceStepT {
  return Object.assign(new ModifierSequenceStepT(), fields);
}

/**
 * What every WAIT on the elevator channel shares: the change to active, seen
 * at the call's floor and on the call's car. The elevator channel is the
 * source, which a step may observe only after the release.
 */
function onElevator(elevatorChannelId: string): Partial<ModifierSequenceStepT> {
  return {
    expectedActive: ActiveState.ACTIVE,
    scope: SequenceStepScope.SAME_POSITION_AND_LIFT,
    observeChannelId: stringToUuidT(elevatorChannelId),
    positionSource: StepPositionSource.SOURCE,
  };
}

/**
 * The secured-floor modifier for one site.
 *
 * Steps, in order. The security side (the read-back input, the relay) uses
 * the payload's floor and the elevator side the call's floor; under the
 * Passthrough value mode the template uses, the two are the same floor.
 *
 *  0. WAIT on the read-back input: the floor reports unsecured (3 s).
 *  1. RELEASE the held call.
 *  2. AWAIT_ACK: the controller acknowledges it (3 s).
 *  3. WAIT on the elevator: the car call is registered, or already was (10 s).
 *  4. WAIT on the elevator: the car is at the floor, or already was (120 s).
 *  5. WAIT on the elevator: the doors opened (30 s).
 *  6. WAIT on the elevator: the doors closed (60 s).
 *  7. SEND the security leaf, active, to the relay — always (10 s).
 *  8. WAIT on the read-back input: the floor reports secured — always (5 s).
 *
 * Throws when a channel is missing, or when the elevator channel is also the
 * relay or the read-back channel: VTS refuses a program that sends to, or
 * waits before the release on, the channel whose call it holds.
 */
export function securedFloorModifier(params: SecuredFloorParams): ChannelModifierT {
  const { elevatorChannelId, relayChannelId, discreteChannelId } = params;
  if (!elevatorChannelId || !relayChannelId || !discreteChannelId) {
    throw new Error(
      "secured-floor template: the elevator, relay and read-back channels are all required",
    );
  }
  if (elevatorChannelId === relayChannelId || elevatorChannelId === discreteChannelId) {
    throw new Error(
      "secured-floor template: the elevator channel must differ from the relay and read-back channels",
    );
  }
  const leaves = CALL_LEAVES[params.variant];
  const lock = params.securityLeaf ?? Control.SecureCarCall;

  const precondition = new ModifierStateConditionT();
  precondition.channelId = stringToUuidT(discreteChannelId);
  precondition.messageContent = MessageContent.Control_ControlMessage;
  precondition.messageValue = lock;
  precondition.expectedActive = ActiveState.ACTIVE;
  precondition.positionSource = StepPositionSource.PAYLOAD;
  precondition.scope = SequenceStepScope.SAME_POSITION;
  // Never act on a security state the input has not reported yet: a run must
  // not secure a floor it did not unsecure.
  precondition.ifUnknown = PreconditionUnknown.ASSUME_NOT_MET;

  // Fire on the call being energized, not on it being dropped.
  const activeCall = new ModifierConditionT();
  activeCall.fieldPath = "/msg/active";
  activeCall.op = ConditionOp.EQ;
  activeCall.valueText = "true";

  const modifier = new ChannelModifierT();
  modifier.id = stringToUuidT(params.id ?? ZERO_UUID_STRING);
  modifier.sourceChannelId = stringToUuidT(elevatorChannelId);
  modifier.targetChannelId = stringToUuidT(relayChannelId);
  modifier.name = params.name;
  modifier.description =
    `Secured-floor interlock (${params.variant} call): unsecures the floor for the call, ` +
    "releases the call, and secures the floor again once the doors have closed.";
  modifier.modifierType = ModifierType.BEFORE;
  modifier.direction = ModifierDirection.OUTBOUND;
  // The message kinds travel as the scalars. The legacy `*_message_type`
  // unions stay NONE: a discriminant with no message table fails the
  // generated verifier, and the Go Object API drops it on unpack anyway.
  modifier.sourceMessageContent = MessageContent.Control_ControlMessage;
  modifier.sourceMessageValue = leaves.energize;
  modifier.targetMessageContent = MessageContent.Control_ControlMessage;
  modifier.payload = buildMessageT(
    ModifierType.BEFORE,
    payloadDraftFromLeaf(MessageContent.Control_ControlMessage, lock),
  );
  modifier.priority = 0;
  modifier.active = true;
  modifier.schemaVersion = SECURED_FLOOR_SCHEMA_VERSION;
  modifier.conditions = [activeCall];
  modifier.actions = [];
  modifier.valueTransformMode = ValueTransformMode.PASSTHROUGH;
  modifier.conflictPolicy = ConflictPolicy.COEXIST;
  // The payload unsecures: the security leaf sent inactive.
  modifier.payloadActive = ActiveState.INACTIVE;
  // A gate that cannot confirm the unsecure drops the call and re-secures.
  modifier.timeoutPolicy = ModifierTimeoutPolicy.DROP;
  modifier.dropAction = ModifierDropAction.REVERT;
  modifier.observeChannelId = stringToUuidT(discreteChannelId);
  modifier.preconditions = [precondition];
  // 0: VTS's ten minutes, ample for the 238 s the steps after the release allow.
  modifier.postReleaseBudgetMs = 0;
  modifier.expectedSequence = [
    programStep({
      expectedMessageContent: MessageContent.Control_ControlMessage,
      expectedMessageValue: lock,
      expectedActive: ActiveState.INACTIVE,
      timeoutMs: 3000,
      label: "Floor unsecured",
    }),
    programStep({ kind: SequenceStepKind.RELEASE, timeoutMs: 0, label: "Release the call" }),
    programStep({ kind: SequenceStepKind.AWAIT_ACK, timeoutMs: 3000, label: "Call acknowledged" }),
    programStep({
      ...onElevator(elevatorChannelId),
      expectedMessageContent: MessageContent.Traffic_TrafficMessage,
      expectedMessageValue: leaves.carCall,
      satisfiedIfCurrent: true,
      timeoutMs: 10000,
      label: leaves.carCallLabel,
    }),
    programStep({
      ...onElevator(elevatorChannelId),
      expectedMessageContent: MessageContent.Traffic_TrafficMessage,
      expectedMessageValue: Traffic.P,
      satisfiedIfCurrent: true,
      timeoutMs: 120000,
      label: "Car at the floor",
    }),
    programStep({
      ...onElevator(elevatorChannelId),
      expectedMessageContent: MessageContent.Usage_UsageMessage,
      expectedMessageValue: leaves.doorOpened,
      timeoutMs: 30000,
      label: leaves.doorOpenedLabel,
    }),
    programStep({
      ...onElevator(elevatorChannelId),
      expectedMessageContent: MessageContent.Usage_UsageMessage,
      expectedMessageValue: leaves.doorClosed,
      timeoutMs: 60000,
      label: leaves.doorClosedLabel,
    }),
    programStep({
      kind: SequenceStepKind.SEND,
      sendMessageContent: MessageContent.Control_ControlMessage,
      sendMessageValue: lock,
      payloadActive: ActiveState.ACTIVE,
      positionSource: StepPositionSource.PAYLOAD,
      alwaysRun: true,
      timeoutMs: 10000,
      label: "Re-secure the floor",
    }),
    programStep({
      expectedMessageContent: MessageContent.Control_ControlMessage,
      expectedMessageValue: lock,
      expectedActive: ActiveState.ACTIVE,
      alwaysRun: true,
      timeoutMs: 5000,
      label: "Floor secured",
    }),
  ];
  return modifier;
}
